import { documentStatus } from "@prismicio/editor-fields"
import { useRealTimeDateDiff } from "@prismicio/editor-support/Date"
import {
  Box,
  Button,
  Dot,
  EditableText,
  Line,
  ScrollArea,
  Tab,
  Text,
  Tooltip,
} from "@prismicio/editor-ui"
import { Suspense, useEffect, useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"

import { DocumentListBackButton } from "../../app/components/DocumentListBackButton"
import { useCollaborationDataFlag } from "../../app/integrations/AmplitudeExperiments"
import { useHeaderOffset } from "../../app/layout/useOffset"
import type { DocumentVersionStatus } from "../../app/services/core"
import { editDocumentPermission } from "../../app/services/permissions"
import { useMyRole } from "../../app/services/role"
import { trackViewHistory } from "../../app/services/tracking"
import { useCollaborationState } from "../collaboration/CollaborationStateProvider"
import { type DocumentData } from "../documentData"
import {
  headerHeight,
  mainContentMaxWidth,
  sidePanelWidth,
} from "../documentLayout"
import { useDocument } from "../DocumentProvider"
import {
  useGoToOldRevisionHistory,
  useIsVersionInMigrationRelease,
} from "../documentRouting"
import { CollaborationAvatarStack } from "./CollaborationAvatarStack"
import { HeaderButtons } from "./HeaderButtons/HeaderButtons"

export function DocumentHeader() {
  const { data, edited, releaseId, status: docStatus } = useDocument()
  const { headerOffset } = useHeaderOffset()

  const newDocIsRelease = Boolean(releaseId)

  const localStatus = getLocalStatus({
    data,
    documentStatus: docStatus,
    edited,
    newDocIsRelease,
  })

  return (
    <Box
      display="flex"
      flexDirection="column"
      position="fixed"
      top={headerOffset}
      height={headerHeight}
      border={{ bottom: true }}
      backgroundColor="grey2"
    >
      <Line color={documentStatus[localStatus].color} />
      <Box
        justifyContent="center"
        width="100vw"
        padding={{ bottom: 16, top: 12 }}
      >
        <Box maxWidth={mainContentMaxWidth} width="100%">
          <Title localStatus={localStatus} />
          <Tabs />
          <CollaborationAvatarStack />
          <HeaderButtons />
        </Box>
      </Box>
    </Box>
  )
}

interface getLocalStatusParams {
  newDocIsRelease: boolean
  edited: boolean
  data: DocumentData
  documentStatus: DocumentVersionStatus
}

function getLocalStatus(params: getLocalStatusParams) {
  const { newDocIsRelease, edited, data, documentStatus } = params

  const displayStatus =
    edited || data.type === "new" ? "unclassified" : data.version.status

  const localStatus =
    documentStatus === "release" || newDocIsRelease ? "release" : displayStatus

  return localStatus
}

interface TitleProps {
  localStatus: DocumentVersionStatus
}

function Title(props: TitleProps) {
  const { localStatus } = props

  const { data, locale, documentTitle, teamSpace } = useDocument()

  const role = useMyRole({ localeId: locale, teamSpaceId: teamSpace.id })
  const canEditDocument = editDocumentPermission[role]

  const { textRef, showTooltip } = useTitleTooltip()

  const hasRevisionHistory = data.type === "existing"

  return (
    <Box
      gap={8}
      width={sidePanelWidth}
      flexShrink={0}
      alignItems="flex-start"
      padding={{ left: 2 }}
    >
      <DocumentListBackButton />
      <Box flexDirection="column" alignItems="flex-start">
        <Box alignItems="center" gap={2}>
          <Dot color={documentStatus[localStatus].color} />
          <Tooltip content={documentTitle.displayedTitle} visible={showTooltip}>
            <EditableText
              ref={textRef}
              aria-label="Edit Document Title"
              variant="bold"
              color="grey12"
              disabled={!canEditDocument}
              value={documentTitle.displayedTitle}
              onBlur={documentTitle.onEditComplete}
              onChange={documentTitle.setDraftTitle}
            />
          </Tooltip>
        </Box>
        {hasRevisionHistory && (
          <Box gap={2}>
            <EditedAt />
            <HistoryLink />
          </Box>
        )}
      </Box>
    </Box>
  )
}

function EditedAt() {
  const lastModifiedDate = useLastModifiedDate()

  const dateDiff = useRealTimeDateDiff(lastModifiedDate)

  return (
    <Text color="grey11" variant="small" noSelect>
      Edited {dateDiff} -
    </Text>
  )
}

function useLastModifiedDate() {
  const { data, draft, status } = useDocument()

  const collaborationState = useCollaborationState()
  const canUseCollaborationData = useCollaborationDataFlag() === "on"

  // it's just a type guard, because new documents
  // don't show the last modified date in the header
  if (data.type !== "existing") return new Date()

  if (
    (status === "unclassified" || status === "release") &&
    collaborationState.enabled &&
    canUseCollaborationData
  ) {
    return draft.editedAt ?? data.version.last_modified_date
  }

  return data.version.last_modified_date
}

function useTitleTooltip() {
  const { documentTitle } = useDocument()

  const textRef = useRef<HTMLDivElement>(null)

  const [showTooltip, setShowTooltip] = useState(false)

  useEffect(() => {
    if (!textRef.current) return

    const input = textRef.current?.querySelector("input")
    if (!input) return

    const inputHasOverflow = input.scrollWidth > input.clientWidth
    setShowTooltip(inputHasOverflow)
  }, [documentTitle.displayedTitle])

  return { showTooltip, textRef }
}

function HistoryLink() {
  return (
    <Suspense
      fallback={
        <Button
          invisible
          disabled
          color="purple"
          size="small"
          textWeight="normal"
        >
          View history
        </Button>
      }
    >
      <HistoryLinkContent />
    </Suspense>
  )
}

function HistoryLinkContent() {
  const { versions, data, locale } = useDocument()
  const isVersionInMigrationRelease = useIsVersionInMigrationRelease()
  const goToOldRevisionHistory = useGoToOldRevisionHistory()

  function openRevisionHistory() {
    if (data.type !== "existing") return

    trackViewHistory({ fromStatus: data.version.status })

    goToOldRevisionHistory({
      documentId: data.meta.id,
      version: data.version,
      locale,
    })
  }

  const hasMigrationReleaseVersion = useMemo(
    () => versions.some(isVersionInMigrationRelease),
    [versions, isVersionInMigrationRelease],
  )

  return (
    <Tooltip
      content="Unavailable for documents in the Migration Release."
      visible={hasMigrationReleaseVersion}
      stableMount
    >
      <Button
        invisible
        disabled={hasMigrationReleaseVersion}
        color="purple"
        size="small"
        onClick={openRevisionHistory}
        textWeight="normal"
      >
        View history
      </Button>
    </Tooltip>
  )
}

function Tabs() {
  const doc = useDocument()

  const [currentSearch] = useSearchParams()

  return (
    <ScrollArea
      direction="horizontal"
      style={{
        flexGrow: 1,
        maxWidth: "100%",
        gap: 8,
      }}
      controls
    >
      {doc.sectionNames.map((sectionName) => {
        const selected = sectionName === doc.sectionName

        const params = new URLSearchParams(currentSearch)
        params.set("section", sectionName)

        return (
          <Tab key={sectionName} selected={selected} asChild noWrap>
            <Link to={`?${params.toString()}`}>
              {sectionName || "Untitled"}
            </Link>
          </Tab>
        )
      })}
    </ScrollArea>
  )
}
